import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookUser,
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Dog,
  ExternalLink,
  FileText,
  HeartPulse,
  Podium,
  Ruler,
  UserRound,
  Weight,
  X
} from "lucide-react";
import {
  Container,
  Row,
  Col,
  Modal,
  ModalBody
} from "reactstrap";

import MainNavbar from "components/Navbars/MainNavbar.js";
import LandingPageHeader from "components/Headers/LandingPageHeader.js";
import DefaultFooter from "components/Footers/DefaultFooter.js";

export function useLandingPageClasses() {
  React.useEffect(() => {
    document.body.classList.add("landing-page");
    document.body.classList.add("sidebar-collapse");
    document.documentElement.classList.remove("nav-open");

    return function cleanup() {
      document.body.classList.remove("landing-page");
      document.body.classList.remove("sidebar-collapse");
    };
  }, []);
}

export function DogPage({ headerBg, title, subtitle, children, pageClassName = "" }) {
  useLandingPageClasses();

  const content = (
    <>
      <MainNavbar />
      <div className="wrapper">
        <LandingPageHeader bg={headerBg} title={title} subtitle={subtitle} />
        {children}
        <DefaultFooter variant="dark" />
      </div>
    </>
  );

  return pageClassName ? <div className={`${pageClassName} site-bg-light-grey`}>{content}</div> : content;
}

function getDogFactIcon(label) {
  const normalizedLabel = label.toLowerCase();

  if (normalizedLabel.includes("breed")) return Dog;
  if (normalizedLabel.includes("born") || normalizedLabel.includes("dob")) return Calendar;
  if (normalizedLabel.includes("height")) return Ruler;
  if (normalizedLabel.includes("weight")) return Weight;
  if (normalizedLabel.includes("owner")) return UserRound;
  if (normalizedLabel.includes("sport")) return Podium;

  return null;
}

export function DogFactsBar({ facts }) {
  return (
    <div
      className="dog-facts-bar"
      style={{ gridTemplateColumns: `repeat(${facts.length}, minmax(0, 1fr))` }}
    >
      {facts.map(fact => {
        const Icon = getDogFactIcon(fact.label);

        return (
          <div className={`dog-fact${Icon ? " dog-fact-with-icon" : ""}`} key={fact.label}>
            {Icon && (
              <span className="dog-fact-icon" aria-hidden="true">
                <Icon size={20} strokeWidth={2.2} />
              </span>
            )}
            <span className="dog-fact-label">{fact.label}</span>
            <strong>{fact.value}</strong>
          </div>
        );
      })}
    </div>
  );
}

export function RichProfileText({ children }) {
  if (typeof children !== "string") {
    return <>{children}</>;
  }

  const linkPattern = /<a\s+href=(['"])(.*?)\1>(.*?)<\/a>/gi;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = linkPattern.exec(children)) !== null) {
    if (match.index > lastIndex) {
      parts.push(children.slice(lastIndex, match.index));
    }

    const href = match[2];
    const label = match[3];
    const isInternalLink = href.startsWith("/");

    parts.push(
      isInternalLink ? (
        <Link className="dog-inline-link" key={`${href}-${match.index}`} to={href}>
          {label}
        </Link>
      ) : (
        <a
          className="dog-inline-link"
          href={href}
          key={`${href}-${match.index}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {label}
        </a>
      )
    );

    lastIndex = linkPattern.lastIndex;
  }

  if (lastIndex < children.length) {
    parts.push(children.slice(lastIndex));
  }

  return <>{parts}</>;
}

function sortCompetitionHighlights(firstHighlight, secondHighlight) {
  return (firstHighlight.order ?? 0) - (secondHighlight.order ?? 0);
}

function getCompetitionHighlightColumns(highlights) {
  const leftColumn = highlights
    .filter(highlight => highlight.desktopColumn === "left")
    .sort(sortCompetitionHighlights);
  const rightColumn = highlights
    .filter(highlight => highlight.desktopColumn === "right")
    .sort(sortCompetitionHighlights);

  highlights
    .filter(highlight => !highlight.desktopColumn)
    .forEach((highlight, index) => {
      if (leftColumn.length <= rightColumn.length) {
        leftColumn.push({ ...highlight, order: highlight.order ?? index });
      } else {
        rightColumn.push({ ...highlight, order: highlight.order ?? index });
      }
    });

  return { leftColumn, rightColumn };
}

function CompetitionSportHighlight({ group }) {
  return (
    <section className="dog-sport-highlight" key={group.sport}>
      <h3>
        <span className="dog-sport-highlight-icon" aria-hidden="true">
          <img alt="" src={group.icon} />
        </span>
        {group.sport}
      </h3>
      {group.groups.map(section => (
        <div className="dog-sport-highlight-group" key={`${group.sport}-${section.label}`}>
          <h4>{section.label}</h4>
          <ul>
            {section.items.map(item => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

export function CompetitionHighlightsGrid({ highlights }) {
  const { leftColumn, rightColumn } = getCompetitionHighlightColumns(highlights);

  return (
    <>
      <div className="dog-competition-grid dog-competition-grid-mobile">
        {highlights.map(group => (
          <CompetitionSportHighlight group={group} key={group.sport} />
        ))}
      </div>
      <div className="dog-competition-grid dog-competition-grid-desktop">
        <div className="dog-competition-column">
          {leftColumn.map(group => (
            <CompetitionSportHighlight group={group} key={group.sport} />
          ))}
        </div>
        <div className="dog-competition-column">
          {rightColumn.map(group => (
            <CompetitionSportHighlight group={group} key={group.sport} />
          ))}
        </div>
      </div>
    </>
  );
}

export function DogProfileOverview({
  name,
  subtitle,
  intro,
  sections,
  stackImage,
  stackAlt,
  stats,
  healthLeft,
  healthRight,
  healthNote,
  pedigreeImage,
  pedigreeAlt,
  clearances,
  actionsClassName = "text-center",
  variant = ""
}) {
  if (variant === "editorial") {
    return (
      <section className="dog-profile-overview dog-profile-overview-editorial">
        <Container>
          <div className="dog-profile-layout">
            <div className="dog-profile-copy">
              <p className="dog-detail-eyebrow">Meet {name}</p>
              <h2>{name}</h2>
              <p className="dog-profile-registered-name">{subtitle}</p>
              <span className="dog-detail-heading-line" aria-hidden="true"></span>
              <p>{intro}</p>
              <div className="dog-profile-story-grid">
                {sections.map(section => (
                  <article className="dog-profile-story" key={section.title}>
                    <h3>{section.title}</h3>
                    {typeof section.content === "string" ? <p><RichProfileText>{section.content}</RichProfileText></p> : section.content}
                  </article>
                ))}
              </div>
            </div>
            <aside className="dog-profile-sidebar">
              <figure className="dog-stack-card">
                <img alt={stackAlt || name} src={stackImage} />
                <figcaption>{stats}</figcaption>
              </figure>
              <div className="dog-health-card">
                <h3>Health Clearances</h3>
                <div className="dog-health-columns">
                  <div>{healthLeft}</div>
                  <div>{healthRight}</div>
                </div>
                {healthNote && <div className="dog-health-note">{healthNote}</div>}
                <DogProfileActions
                  dogName={name}
                  pedigreeImage={pedigreeImage}
                  pedigreeAlt={pedigreeAlt || `${name} pedigree`}
                  clearances={clearances}
                  className="dog-profile-actions"
                />
              </div>
            </aside>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <div className="wrapper">
      <div className="section section-story-overview section-dog">
        <Container>
          <Row>
            <Col className="ml-auto mr-auto" md="6">
              <div className="name-desc">
                <h2 className="title">{name}</h2>
                <h3 className="subtitle">{subtitle}</h3>
                <div>{intro}</div>
                {sections.map(section => (
                  <div key={section.title}>
                    <h4 className="subtitle">{section.title}</h4>
                    {section.content}
                  </div>
                ))}
              </div>
            </Col>
            <Col className="ml-auto mr-auto align-middle" md="5">
              <div className="health-clearances">
                <Row>
                  <img alt={stackAlt || name} className="img-fluid" src={stackImage} />
                  <div className="small">{stats}</div>
                </Row>
                <h3 className="title text-center">Health Clearances</h3>
                <Row>
                  <div className="sec-60">{healthLeft}</div>
                  <div className="sec-40">{healthRight}</div>
                  {healthNote}
                </Row>
                <DogProfileActions
                  dogName={name}
                  pedigreeImage={pedigreeImage}
                  pedigreeAlt={pedigreeAlt || `${name} pedigree`}
                  clearances={clearances}
                  className={actionsClassName}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}

export function DogProfileActions({
  dogName = "Dog",
  pedigreeImage,
  pedigreeAlt,
  pedigreeData,
  clearances,
  className
}) {
  const [isPedigreeOpen, setIsPedigreeOpen] = React.useState(false);
  const [isClearancesOpen, setIsClearancesOpen] = React.useState(false);
  const pedigreeTriggerRef = React.useRef(null);
  const healthTriggerRef = React.useRef(null);
  const pedigree = React.useMemo(
    () => normalizePedigreeData({ dogName, pedigreeData, pedigreeImage, pedigreeAlt }),
    [dogName, pedigreeAlt, pedigreeData, pedigreeImage]
  );

  const closeClearances = () => {
    setIsClearancesOpen(false);
    window.setTimeout(() => {
      healthTriggerRef.current?.focus();
    }, 0);
  };

  const closePedigree = () => {
    setIsPedigreeOpen(false);
    window.setTimeout(() => {
      pedigreeTriggerRef.current?.focus();
    }, 0);
  };

  return (
    <div className={className}>
      <br />
      {pedigree?.image && (
        <button
          ref={pedigreeTriggerRef}
          className="btn btn-round action-btn"
          type="button"
          onClick={() => setIsPedigreeOpen(true)}
        >
          <BookUser size={19} strokeWidth={2.2} aria-hidden="true" />
          <span>View Pedigree</span>
          <ArrowRight className="dog-action-btn-arrow" size={18} strokeWidth={2.4} aria-hidden="true" />
        </button>
      )}
      {clearances && clearances.length > 0 && (
        <button
          ref={healthTriggerRef}
          className="btn btn-round action-btn"
          type="button"
          onClick={() => setIsClearancesOpen(true)}
        >
          <ClipboardCheck size={19} strokeWidth={2.2} aria-hidden="true" />
          <span>Health Documents</span>
          <ArrowRight className="dog-action-btn-arrow" size={18} strokeWidth={2.4} aria-hidden="true" />
        </button>
      )}
      <PedigreeModal
        dogName={dogName}
        isOpen={isPedigreeOpen}
        pedigree={pedigree}
        toggle={closePedigree}
      />
      <ClearanceCarouselModal
        dogName={dogName}
        clearances={clearances || []}
        isOpen={isClearancesOpen}
        toggle={closeClearances}
      />
    </div>
  );
}

const primaryHealthScreeningRows = [
  { label: "Hips", aliases: ["hips"] },
  { label: "Elbows", aliases: ["elbows"] },
  { label: "Shoulders", aliases: ["shoulders"] },
  { label: "Eyes", aliases: ["eyes"] },
  { label: "Knees", aliases: ["knees", "patellas", "patella"] },
  { label: "Hearing (BAER)", aliases: ["hearing", "baer"] }
];

function normalizeHealthLabel(label = "") {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function findHealthGroup(healthGroups, matcher) {
  return (healthGroups || []).find(group => matcher(group.title || ""));
}

function findHealthItem(items, aliases) {
  return items.find(item => {
    const normalizedItemLabel = normalizeHealthLabel(item.label);

    return aliases.some(alias => normalizedItemLabel.includes(normalizeHealthLabel(alias)));
  });
}

function getHealthScreeningRows(healthGroups) {
  const screeningGroup = findHealthGroup(
    healthGroups,
    title => title.toLowerCase().includes("screening") || title.toLowerCase().includes("orthopedic")
  );
  const items = screeningGroup?.items || [];
  const usedLabels = new Set();

  const primaryRows = primaryHealthScreeningRows.flatMap(row => {
    const item = findHealthItem(items, row.aliases);

    if (!item) {
      return [];
    }

    usedLabels.add(item.label);

    return {
      label: row.label,
      value: item.value || "TBD"
    };
  });

  const extraRows = items
    .filter(item => !usedLabels.has(item.label))
    .map(item => ({
      label: item.label,
      value: item.value || "TBD"
    }));

  return {
    rows: [...primaryRows, ...extraRows],
    note: screeningGroup?.note
  };
}

function getDnaPanelData(healthGroups) {
  const dnaGroup = findHealthGroup(healthGroups, title => title.toLowerCase().includes("dna"));
  const columns = dnaGroup?.columns || [];
  const breedColumn = columns.find(column => (column.title || "").toLowerCase() === "breeds");
  const dnaItems = columns.length > 0
    ? columns
      .filter(column => column !== breedColumn)
      .flatMap(column => column.items || [])
    : dnaGroup?.items || [];

  return {
    breedComposition: breedColumn?.items || [],
    clearTraits: dnaItems.filter(item => (item.value || "").toLowerCase().includes("clear")),
    carrierTraits: dnaItems.filter(item => (item.value || "").toLowerCase().includes("carrier")),
    note: dnaGroup?.note
  };
}

function isPurebredBorderCollie(breed) {
  return normalizeHealthLabel(breed) === "bordercollie";
}

function DogHealthActionButton({ children, icon: Icon, onClick, refProp }) {
  return (
    <button
      ref={refProp}
      className="btn action-btn dog-health-compact-action"
      type="button"
      onClick={onClick}
    >
      <Icon size={19} strokeWidth={2.2} aria-hidden="true" />
      <span>{children}</span>
      <ArrowRight className="dog-action-btn-arrow" size={18} strokeWidth={2.4} aria-hidden="true" />
    </button>
  );
}

function DogHealthTraitList({ items, emptyText = "None" }) {
  if (!items || items.length === 0) {
    return <p className="dog-health-empty">{emptyText}</p>;
  }

  return (
    <ul className="dog-health-trait-list">
      {items.map(item => (
        <li key={`${item.label}-${item.value}`}>{item.label}</li>
      ))}
    </ul>
  );
}

export function DogHealthGenetics({
  dogName,
  breed,
  healthGroups,
  clearances,
  pedigreeImage,
  pedigreeAlt,
  pedigreeData
}) {
  const [isPedigreeOpen, setIsPedigreeOpen] = React.useState(false);
  const [isClearancesOpen, setIsClearancesOpen] = React.useState(false);
  const pedigreeTriggerRef = React.useRef(null);
  const healthTriggerRef = React.useRef(null);
  const { rows: screeningRows, note: screeningNote } = getHealthScreeningRows(healthGroups);
  const { breedComposition, clearTraits, carrierTraits, note: dnaNote } = getDnaPanelData(healthGroups);
  const pedigree = React.useMemo(
    () => normalizePedigreeData({ dogName, pedigreeData, pedigreeImage, pedigreeAlt }),
    [dogName, pedigreeAlt, pedigreeData, pedigreeImage]
  );
  const shouldShowBreedComposition = breedComposition.length > 0 && !isPurebredBorderCollie(breed);

  const closeClearances = () => {
    setIsClearancesOpen(false);
    window.setTimeout(() => {
      healthTriggerRef.current?.focus();
    }, 0);
  };

  const closePedigree = () => {
    setIsPedigreeOpen(false);
    window.setTimeout(() => {
      pedigreeTriggerRef.current?.focus();
    }, 0);
  };

  return (
    <div className="dog-health-compact-panel">
      <div className="dog-health-compact-heading">
        <h2>Health & Genetics</h2>
        <span aria-hidden="true"></span>
      </div>
      <div className="dog-health-compact-layout">
        <section className="dog-health-screening-panel">
          <h3>Health Screening</h3>
          <dl className="dog-health-screening-list">
            {screeningRows.map(row => (
              <div className="dog-health-screening-row" key={row.label}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
          {screeningNote && <p className="dog-health-compact-note">{screeningNote}</p>}
          <div className="dog-health-compact-actions">
            {clearances && clearances.length > 0 && (
              <DogHealthActionButton
                refProp={healthTriggerRef}
                icon={ClipboardCheck}
                onClick={() => setIsClearancesOpen(true)}
              >
                View Health Documents
              </DogHealthActionButton>
            )}
            {pedigree?.image && (
              <DogHealthActionButton
                refProp={pedigreeTriggerRef}
                icon={BookUser}
                onClick={() => setIsPedigreeOpen(true)}
              >
                View Pedigree
              </DogHealthActionButton>
            )}
          </div>
        </section>
        <section className="dog-health-genetics-panel">
          {shouldShowBreedComposition && (
            <div className="dog-health-breed-composition">
              <h3>Breed Composition</h3>
              <div>
                {breedComposition.map(item => (
                  <div className="dog-health-breed-item" key={item.label}>
                    <strong>{item.value}</strong>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="dog-health-dna-summary">
            <h3>Genetic Testing</h3>
            <section className="dog-health-trait-group">
              <h4>
                <span className="dog-health-clear-icon" aria-hidden="true">
                  <Check size={13} strokeWidth={3} />
                </span>
                Clear
              </h4>
              <DogHealthTraitList items={clearTraits} emptyText="None" />
            </section>
            <section className="dog-health-trait-group dog-health-carrier-group">
              <h4>
                <span className="dog-health-carrier-icon" aria-hidden="true">–</span>
                Carrier
              </h4>
              <DogHealthTraitList items={carrierTraits} emptyText="None" />
            </section>
            {dnaNote && <p className="dog-health-compact-note">{dnaNote}</p>}
            {clearances && clearances.length > 0 && (
              <p className="dog-health-dna-details">
                View DNA details in the{" "}
                <button type="button" onClick={() => setIsClearancesOpen(true)}>
                  health documents
                </button>.
              </p>
            )}
          </div>
        </section>
      </div>
      <PedigreeModal
        dogName={dogName}
        isOpen={isPedigreeOpen}
        pedigree={pedigree}
        toggle={closePedigree}
      />
      <ClearanceCarouselModal
        dogName={dogName}
        clearances={clearances || []}
        isOpen={isClearancesOpen}
        toggle={closeClearances}
      />
    </div>
  );
}

function normalizePedigreeData({ dogName, pedigreeData, pedigreeImage, pedigreeAlt }) {
  if (pedigreeData?.image) {
    return {
      alt: `${dogName} pedigree`,
      linkedDogs: [],
      ...pedigreeData
    };
  }

  if (!pedigreeImage) {
    return null;
  }

  return {
    image: pedigreeImage,
    alt: pedigreeAlt || `${dogName} pedigree`,
    linkedDogs: []
  };
}

function PedigreeLinkedDogRow({ dog }) {
  return (
    <article className="pedigree-linked-dog-entry">
      <div className="pedigree-linked-dog-row">
        {dog.image && (
          <img
            alt={`${dog.name}${dog.breed ? `, ${dog.breed}` : ""}`}
            className="pedigree-linked-dog-image"
            src={dog.image}
          />
        )}
        <div className="pedigree-linked-dog-copy">
          <div className="pedigree-linked-dog-title">
            <h3>{dog.name}</h3>
            {dog.relationship && <span>{dog.relationship}</span>}
          </div>
          {dog.breed && <p>{dog.breed}</p>}
          {dog.profileHref && (
            <Link aria-label={`View ${dog.name} profile`} className="pedigree-linked-dog-action" to={dog.profileHref}>
              View profile <ArrowRight aria-hidden="true" size={15} strokeWidth={2.4} />
            </Link>
          )}
        </div>
      </div>
      {dog.litter && (
        <Link
          aria-label={`View ${dog.litter.label} litter`}
          className="pedigree-linked-dog-litter"
          to={dog.litter.href}
        >
          <span className="pedigree-linked-litter-label">From litter</span>
          <span className="pedigree-linked-litter-content">
            {dog.litter.image && (
              <img
                alt={`${dog.litter.label} announcement`}
                className="pedigree-linked-litter-image"
                src={dog.litter.image}
              />
            )}
            <strong>{dog.litter.label}</strong>
            <ArrowRight aria-hidden="true" size={15} strokeWidth={2.4} />
          </span>
        </Link>
      )}
    </article>
  );
}

export function PedigreeModal({ dogName, isOpen, pedigree, toggle }) {
  if (!pedigree?.image) {
    return null;
  }

  const linkedDogs = pedigree.linkedDogs || [];

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className="pedigree-modal"
      contentClassName="pedigree-modal-content"
      centered
    >
      <section className="pedigree-viewer">
        <header className="pedigree-modal-header">
          <div>
            <h2>Pedigree</h2>
          </div>
          <button
            aria-label="Close pedigree"
            className="pedigree-modal-close"
            type="button"
            onClick={toggle}
          >
            <X aria-hidden="true" size={24} strokeWidth={2.4} />
          </button>
        </header>
        <ModalBody className="pedigree-modal-body">
          <div className={`pedigree-modal-main${linkedDogs.length > 0 ? " has-linked-dogs" : ""}`}>
            {linkedDogs.length > 0 && (
              <section className="pedigree-linked-dogs">
                <h3>Endgame Dogs in This Pedigree</h3>
                <div>
                  {linkedDogs.map(dog => (
                    <PedigreeLinkedDogRow dog={dog} key={dog.id || dog.name} />
                  ))}
                </div>
              </section>
            )}
            <div className="pedigree-image-panel">
              <div className="pedigree-image-stage">
                <img alt={pedigree.alt || `${dogName} pedigree`} src={pedigree.image} />
                <a
                  aria-label={`Open ${dogName}'s pedigree full size`}
                  className="pedigree-full-size-link"
                  href={pedigree.image}
                  rel="noreferrer"
                  target="_blank"
                >
                  <ExternalLink aria-hidden="true" size={15} strokeWidth={2.2} />
                  Open full size
                </a>
              </div>
            </div>
          </div>
          {pedigree.imageCredit && (
            <footer className="pedigree-modal-footer">
              <p>{pedigree.imageCredit}</p>
            </footer>
          )}
        </ModalBody>
      </section>
    </Modal>
  );
}

const healthDocumentCategoryOrder = ["Orthopedic", "Cardiac", "Eyes", "Genetic", "Other"];

function getHealthDocumentImage(document) {
  return typeof document === "string" ? document : document.image || document.src;
}

function normalizeHealthDocuments(clearances, dogName) {
  return clearances
    .map((document, index) => {
      const image = getHealthDocumentImage(document);

      if (!image) {
        return null;
      }

      if (typeof document === "object") {
        const label = document.label || `${dogName} health document ${index + 1}`;

        return {
          id: document.id || `${dogName.toLowerCase()}-health-document-${index + 1}`,
          label,
          shortLabel: document.shortLabel || label,
          category: document.category || "Other",
          date: document.date,
          image,
          alt: document.alt || `${dogName} ${label}`,
          detailLabel: document.detailLabel
        };
      }

      return {
        id: `${dogName.toLowerCase()}-health-certificate-${index + 1}`,
        label: `${dogName} Health Certificate ${index + 1}`,
        shortLabel: `Certificate ${index + 1}`,
        category: "Other",
        image,
        alt: `${dogName} health certificate ${index + 1}`
      };
    })
    .filter(Boolean);
}

function getHealthDocumentDateTime(date) {
  if (!date) {
    return null;
  }

  const parsedDate = Date.parse(date);

  return Number.isNaN(parsedDate) ? null : parsedDate;
}

function getGroupedHealthDocuments(documents) {
  return healthDocumentCategoryOrder
    .map(category => ({
      category,
      documents: documents
        .map((document, index) => ({ ...document, index }))
        .filter(document => document.category === category)
        .sort((firstDocument, secondDocument) => {
          const firstDate = getHealthDocumentDateTime(firstDocument.date);
          const secondDate = getHealthDocumentDateTime(secondDocument.date);

          if (firstDate === null && secondDate === null) {
            return firstDocument.index - secondDocument.index;
          }

          if (firstDate === null) {
            return 1;
          }

          if (secondDate === null) {
            return -1;
          }

          return secondDate - firstDate || firstDocument.index - secondDocument.index;
        })
    }))
    .filter(group => group.documents.length > 0);
}

function HealthDocumentMeta({ document }) {
  return (
    <>
      {document.detailLabel || document.category}
      {document.date && <> · {document.date}</>}
    </>
  );
}

function ClearanceCarouselModal({ dogName, clearances, isOpen, toggle }) {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const selectorRefs = React.useRef([]);
  const documents = React.useMemo(
    () => normalizeHealthDocuments(clearances, dogName),
    [clearances, dogName]
  );
  const groupedDocuments = React.useMemo(
    () => getGroupedHealthDocuments(documents),
    [documents]
  );
  const activeDocument = documents[activeIndex];
  const previousDocument = documents[activeIndex - 1];
  const nextDocument = documents[activeIndex + 1];

  React.useEffect(() => {
    if (isOpen) {
      setActiveIndex(0);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;

    selectorRefs.current[activeIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center"
    });
  }, [activeIndex, isOpen]);

  React.useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = event => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setActiveIndex(current => Math.max(0, current - 1));
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        setActiveIndex(current => Math.min(documents.length - 1, current + 1));
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [documents.length, isOpen]);

  React.useEffect(() => {
    [previousDocument, nextDocument].filter(Boolean).forEach(document => {
      const image = new Image();
      image.src = document.image;
    });
  }, [previousDocument, nextDocument]);

  if (!documents.length || !activeDocument) {
    return null;
  }

  const goToPreviousDocument = () => setActiveIndex(current => Math.max(0, current - 1));
  const goToNextDocument = () => setActiveIndex(current => Math.min(documents.length - 1, current + 1));

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className="health-documents-modal"
      contentClassName="health-documents-modal-content"
      centered
    >
      <section className="health-documents-viewer" aria-live="polite">
        <header className="health-documents-header">
          <div className="health-documents-title-group">
            <HeartPulse aria-hidden="true" size={24} strokeWidth={2.1} />
            <div>
              <h2>{documents.length} Health Documents</h2>
            </div>
          </div>
          <div className="health-documents-header-actions">
            <button
              className="health-documents-close"
              type="button"
              onClick={toggle}
              aria-label="Close health documents"
            >
              <X aria-hidden="true" size={22} strokeWidth={2.2} />
            </button>
          </div>
        </header>

        <div className="health-documents-layout">
          <aside className="health-documents-sidebar" aria-label="Health document list">
            {groupedDocuments.map(group => (
              <section className="health-documents-sidebar-group" key={group.category}>
                <h3>{group.category}</h3>
                <div>
                  {group.documents.map(document => (
                    <button
                      className={`health-document-list-item${document.index === activeIndex ? " active" : ""}`}
                      type="button"
                      key={document.id}
                      onClick={() => setActiveIndex(document.index)}
                      aria-label={`View ${document.label}`}
                      aria-current={document.index === activeIndex ? "true" : undefined}
                    >
                      <FileText aria-hidden="true" size={17} strokeWidth={2} />
                      <span>
                        <strong>{document.label}</strong>
                        <small><HealthDocumentMeta document={document} /></small>
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </aside>

          <main className="health-document-main">
            <div className="health-document-active-heading">
              <div>
                <h3>{activeDocument.label}</h3>
                <p><HealthDocumentMeta document={activeDocument} /></p>
              </div>
              <a
                className="health-documents-full-size"
                href={activeDocument.image}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${activeDocument.label} full size`}
              >
                <ExternalLink aria-hidden="true" size={16} strokeWidth={2.2} />
                <span>Open full size</span>
              </a>
            </div>

            <div className="health-document-stage">
              <img src={activeDocument.image} alt={activeDocument.alt} />
            </div>
          </main>
        </div>

        <footer className="health-documents-footer">
          <button
            className="health-document-nav-btn"
            type="button"
            onClick={goToPreviousDocument}
            disabled={!previousDocument}
            aria-label={previousDocument ? `Previous document: ${previousDocument.label}` : "Previous document"}
          >
            <ChevronLeft aria-hidden="true" size={17} strokeWidth={2.4} />
            Previous
          </button>
          <span>{activeIndex + 1} of {documents.length}</span>
          <button
            className="health-document-nav-btn"
            type="button"
            onClick={goToNextDocument}
            disabled={!nextDocument}
            aria-label={nextDocument ? `Next document: ${nextDocument.label}` : "Next document"}
          >
            Next
            <ChevronRight aria-hidden="true" size={17} strokeWidth={2.4} />
          </button>
        </footer>

        <nav className="health-document-mobile-selector" aria-label="Health document selector">
          {documents.map((document, index) => (
            <button
              ref={node => {
                selectorRefs.current[index] = node;
              }}
              className={`health-document-selector-item${index === activeIndex ? " active" : ""}`}
              type="button"
              key={document.id}
              onClick={() => setActiveIndex(index)}
              aria-label={`View ${document.label}`}
              aria-current={index === activeIndex ? "true" : undefined}
            >
              <strong>{document.shortLabel}</strong>
              <small>{document.category}</small>
            </button>
          ))}
        </nav>
      </section>
    </Modal>
  );
}

export function SectionDividerImage({ image }) {
  return (
    <div className="section section-dog text-center">
      <div
        className="image-fullw section-sep-image"
        style={{ backgroundImage: "url(" + image + ")" }}
      >
        <div className="wrapper"></div>
      </div>
    </div>
  );
}

export function SportCardGrid({ cards, variant = "" }) {
  if (variant === "editorial") {
    return (
      <div className="dog-sport-card-grid">
        {cards.map(card => (
          <article className="dog-sport-card" key={card.title}>
            <img alt={card.alt || card.title} src={card.image} />
            <div className="dog-sport-card-copy">
              <h3>{card.title}</h3>
              <p>{card.description}</p>
            </div>
          </article>
        ))}
      </div>
    );
  }

  return (
    <Row>
      {cards.map(card => (
        <Col md="4" className="mb-5" key={card.title}>
          <div>
            <img alt={card.alt || card.title} className="img-fluid img-raised" src={card.image} />
            <h4 className="title">{card.title}</h4>
            <p className="description">{card.description}</p>
          </div>
        </Col>
      ))}
    </Row>
  );
}

export function DogGallerySection({ title = "Photo Gallery", images, backgroundImage, children }) {
  return (
    <div className="section-dog">
      <div
        className="image-fullw section-gallery-bg"
        style={backgroundImage ? { backgroundImage: "url(" + backgroundImage + ")" } : { padding: "100px 0" }}
      >
        {title && <h2 className="title text-center">{title}</h2>}
        {children}
        {images}
      </div>
    </div>
  );
}

const dogStatusConfig = {
  "co-owned": {
    label: "Co-owned",
    className: "dog-portfolio-status-co-owned"
  },
  "not-bred": {
    label: "Not bred",
    className: "dog-portfolio-status-not-bred"
  },
  "for stud": {
    label: "For stud",
    className: "dog-portfolio-status-for-stud"
  }
};

export function DogListingGrid({ dogs, rowClassName = "", cardClassName = "" }) {
  const isPortfolioCard = cardClassName.includes("dog-portfolio-card");

  return (
    <Row className={rowClassName}>
      {dogs.map(dog => {
        const status = dog.status ? dogStatusConfig[dog.status] : null;

        return (
          <Col md="4" className="mb-4" key={dog.href}>
            <Link
              aria-label={`View ${dog.name}'s profile`}
              to={dog.href}
              className={`dogs-link ${cardClassName}`.trim()}
            >
              {isPortfolioCard ? (
                <>
                  <span className="dog-portfolio-image">
                    {status && (
                      <span className={`dog-portfolio-status ${status.className}`}>
                        {status.label}
                      </span>
                    )}
                    <img
                      alt={`${dog.name}, ${dog.breed || "Endgame Sport Dog"}`}
                      src={dog.image}
                      style={dog.imagePosition ? { objectPosition: dog.imagePosition } : undefined}
                    />
                    <span className="dog-portfolio-nameplate">
                      <span className="dog-portfolio-nameplate-copy">
                        <span className="dog-portfolio-name">{dog.name}</span>
                        {dog.breed && <span className="dog-portfolio-breed">{dog.breed}</span>}
                      </span>
                      <ArrowRight aria-hidden="true" size={26} strokeWidth={2.25} />
                    </span>
                  </span>
                </>
              ) : cardClassName ? (
                <div className="listing-card-copy">
                  {dog.meta && <p className="listing-card-meta">{dog.meta}</p>}
                  <h4>{dog.name}</h4>
                </div>
              ) : (
                <>
                  <img alt={dog.name} className="img-fluid img-raised" src={dog.image} />
                  <div className="dogs-name">
                    <span>{dog.name}</span>
                  </div>
                </>
              )}
            </Link>
          </Col>
        );
      })}
    </Row>
  );
}
